import type { ReactNode } from 'react';
import { useI18n } from '../../lib/i18n';

/**
 * Faz 8 rapor ekranlarının ortak parçaları: tablo hücre sınıfları, yüzde biçimi, özet kutucuğu, bölüm ve tablo.
 */

export const th = 'px-3 py-2 text-start font-medium';
export const thR = 'px-3 py-2 text-end font-medium';
export const td = 'px-3 py-2';
export const tdR = 'px-3 py-2 text-end tabular';

const TAGS: Record<string, string> = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' };

/** Baz puanı yüzde olarak yazar ("%87,2", "-%2.312,8"); boşsa "—". Yalnızca gösterim. */
export function usePct() {
  const { locale } = useI18n();
  const f = new Intl.NumberFormat(TAGS[locale] ?? 'tr-TR', { style: 'percent', maximumFractionDigits: 1 });
  return (bps: number | null) => (bps === null ? '—' : f.format(bps / 10_000));
}

/** Karşılaştırmasız özet kutucuğu: ad, değer ve altında kısa açıklama. */
export function Figure({
  label,
  value,
  sub,
  help,
}: {
  label: string;
  value: string;
  sub?: string;
  help?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-line bg-surface p-4">
      <div className="flex h-6 items-center gap-1 text-sm text-ink-3">
        {label}
        {help}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-2">{sub}</div>}
    </div>
  );
}

export function Section({ title, help, children }: { title: string; help?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-1 text-base font-semibold text-ink">
        {title}
        {help}
      </h2>
      {children}
    </section>
  );
}

export function Table({
  caption,
  head,
  children,
}: {
  caption: string;
  head: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-surface-2 text-ink-2">{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
